import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { retryUnlessRecordUnavailable } from "@/common/utils/record-unavailable-error";
import { PROVIDER_PROFILE_CREDENTIAL_FIELDS } from "@/features/profile/constants";
import type { ProviderProfilePageProps } from "@/features/profile/components/ProviderProfilePage";

type ProviderProfileData = Pick<
  ProviderProfilePageProps,
  "credentials" | "jobPreferences" | "sharePermissions"
>;

type UseProviderProfileDataResult = {
  isLoading: boolean;
  error: unknown;
  data: ProviderProfileData | null;
};

export const useProviderProfileData = (
  userId: string,
  organizationBusinessUnitId?: string,
): UseProviderProfileDataResult => {
  const trpc = useTRPC();

  const credentialsQuery = useQuery(
    trpc.credentialFiles.getCredentialsFields.queryOptions(
      {
        fieldCodes: PROVIDER_PROFILE_CREDENTIAL_FIELDS,
        switchUserId: userId,
        organizationBusinessUnitId,
      },
      { retry: retryUnlessRecordUnavailable },
    ),
  );

  const jobPreferencesQuery = useQuery(
    trpc.profile.getJobPreferences.queryOptions({
      userId,
    }),
  );

  const sharePermissionsQuery = useQuery(
    trpc.profile.getPermissions.queryOptions({
      userId,
    }),
  );

  const isLoading =
    credentialsQuery.isLoading ||
    jobPreferencesQuery.isLoading ||
    sharePermissionsQuery.isLoading;

  const error =
    credentialsQuery.error ??
    jobPreferencesQuery.error ??
    sharePermissionsQuery.error ??
    null;

  const data = useMemo(() => {
    if (!credentialsQuery.data || !sharePermissionsQuery.data) {
      return null;
    }

    return {
      credentials: credentialsQuery.data,
      jobPreferences: jobPreferencesQuery.data,
      sharePermissions: sharePermissionsQuery.data,
    };
  }, [
    credentialsQuery.data,
    jobPreferencesQuery.data,
    sharePermissionsQuery.data,
  ]);

  return {
    isLoading,
    error,
    data,
  };
};
